/**
 * KVG (Krankenversicherungsgesetz) / KLV (Krankenpflege-Leistungsverordnung)
 * hourly rates for Spitex home care, Switzerland, 2026.
 *
 * Sources:
 *   - KLV Art. 7a (EDI ordinance on benefits)
 *   - BFS / BAG tariff schedules 2026
 *   - Spitex Frei GmbH reference rates (verified 2026-04)
 *
 * `spitexTariff` is the full hourly rate a KVG-anerkannt Spitex provider
 * charges (Spitex Frei rates). `kvgContribution` is the fixed per-hour rate
 * the Krankenkasse (OKP/KVG) pays under Art. 25a KVG. The cantonal
 * contribution ("Restfinanzierung") covers the remainder plus an optional
 * patient co-pay of up to CHF 8/day under the 2026 revision of Art. 25a.
 *
 * NOTE: rates are hardcoded for 2026. For 2027+ update this file and the
 * visible disclaimer on the landing page together. No API calls — rates
 * change once per year by regulation.
 */

export type ServiceType =
  | 'behandlungspflege'
  | 'grundpflege'
  | 'abklaerung'
  | 'hauswirtschaft';

export interface ServiceRate {
  /** German label for dropdown. */
  label: string;
  /** Short description for helper text under the dropdown. */
  description: string;
  /** Hourly tariff charged by a KVG-anerkannt Spitex provider (CHF). */
  spitexTariff: number;
  /** Fixed hourly amount paid by the Krankenkasse (CHF). `null` = not KVG-covered. */
  kvgContribution: number | null;
}

export const SERVICE_RATES: Record<ServiceType, ServiceRate> = {
  behandlungspflege: {
    label: 'Behandlungspflege',
    description:
      'Medizinische Pflege nach aerztlicher Verordnung (Infusionen, Spritzen, Wundversorgung).',
    spitexTariff: 80.5,
    kvgContribution: 63.0,
  },
  grundpflege: {
    label: 'Grundpflege',
    description:
      'Koerperpflege, An- und Auskleiden, Mobilisation, Ernaehrung, Ausscheidung.',
    spitexTariff: 65.4,
    kvgContribution: 54.6,
  },
  abklaerung: {
    label: 'Abklaerung und Beratung',
    description:
      'Pflegerische Erstabklaerung, Beratung fuer Patient und Angehoerige.',
    spitexTariff: 83.2,
    kvgContribution: 76.9,
  },
  hauswirtschaft: {
    label: 'Hauswirtschaft und Betreuung',
    description:
      'Reinigung, Waesche, Einkaufen, Mahlzeiten. Nicht KVG-gedeckt, privat zu bezahlen.',
    spitexTariff: 48.0,
    kvgContribution: null,
  },
};

/**
 * Maximum patient co-payment per day under Art. 25a KVG (revised 2026).
 * Originally CHF 15.35/day; cantons may waive or reduce this.
 */
export const MAX_COPAY_PER_DAY_CHF = 8.0;

/** Approximate number of weeks per month for CHF/month estimates. */
export const WEEKS_PER_MONTH = 4.33;

/**
 * Supported cantons on the calculator. Matches the seven cantons where
 * Spitex Frei holds Betriebsbewilligungen (see SpitexProviderDisclosure).
 */
export type CantonCode = 'ZH' | 'AG' | 'BE' | 'LU' | 'SG' | 'ZG' | 'SZ';

export interface CantonInfo {
  code: CantonCode;
  label: string;
  /**
   * Approximate share of the Spitex-tariff-to-KVG gap that the canton
   * covers (Restfinanzierung). Real values depend on municipality and
   * the patient's Pflegebedarf. These are pragmatic averages used for a
   * non-binding estimate.
   */
  restfinanzierungShare: number;
  /**
   * Whether the canton applies the patient co-pay (up to CHF 8/day).
   * `true` = co-pay charged. `false` = waived by cantonal regulation.
   */
  appliesCopay: boolean;
}

export const CANTONS: CantonInfo[] = [
  {
    code: 'ZH',
    label: 'Zuerich',
    restfinanzierungShare: 1.0,
    appliesCopay: true,
  },
  {
    code: 'AG',
    label: 'Aargau',
    restfinanzierungShare: 0.95,
    appliesCopay: true,
  },
  { code: 'BE', label: 'Bern', restfinanzierungShare: 0.9, appliesCopay: true },
  {
    code: 'LU',
    label: 'Luzern',
    restfinanzierungShare: 0.95,
    appliesCopay: true,
  },
  {
    code: 'SG',
    label: 'St. Gallen',
    restfinanzierungShare: 0.9,
    appliesCopay: true,
  },
  { code: 'ZG', label: 'Zug', restfinanzierungShare: 1.0, appliesCopay: false },
  {
    code: 'SZ',
    label: 'Schwyz',
    restfinanzierungShare: 0.9,
    appliesCopay: true,
  },
];

export interface CostBreakdown {
  /** Total Spitex tariff before any coverage (CHF / month). */
  monthlyTotal: number;
  /** Krankenkasse share (CHF / month). */
  monthlyKvg: number;
  /** Cantonal / communal Restfinanzierung (CHF / month). */
  monthlyCanton: number;
  /** Patient out-of-pocket (CHF / month), incl. co-pay if applicable. */
  monthlyOutOfPocket: number;
  /** Hours per month (weeksPerMonth * weeklyHours). */
  monthlyHours: number;
  /** True if this service is covered by KVG (OKP). */
  isKvgCovered: boolean;
}

/**
 * Compute the out-of-pocket estimate for a given calculator input.
 *
 * Formula (KVG-covered):
 *   monthlyTotal  = spitexTariff * weeklyHours * WEEKS_PER_MONTH
 *   monthlyKvg    = kvgContribution * weeklyHours * WEEKS_PER_MONTH
 *   gap           = monthlyTotal - monthlyKvg
 *   monthlyCanton = gap * restfinanzierungShare
 *   copay         = appliesCopay ? daysInMonthWithCare * MAX_COPAY_PER_DAY_CHF : 0
 *                   (capped so canton + copay never exceeds gap)
 *                   (patient can be exempt via franchiseFulfilled heuristic:
 *                    a patient with fulfilled franchise still pays the
 *                    statutory Patientenbeteiligung — franchise only affects
 *                    OKP coinsurance, not the Art. 25a co-pay. We treat the
 *                    checkbox as a reassurance signal; no effect on copay.)
 *   monthlyOutOfPocket = monthlyTotal - monthlyKvg - monthlyCanton
 *
 * Non-KVG (Hauswirtschaft):
 *   monthlyTotal        = spitexTariff * weeklyHours * WEEKS_PER_MONTH
 *   monthlyKvg          = 0
 *   monthlyCanton       = 0
 *   monthlyOutOfPocket  = monthlyTotal
 */
export function computeCost(args: {
  serviceType: ServiceType;
  weeklyHours: number;
  canton: CantonCode;
  franchiseFulfilled: boolean;
}): CostBreakdown {
  const service = SERVICE_RATES[args.serviceType];
  const cantonInfo = CANTONS.find((c) => c.code === args.canton) ?? CANTONS[0]!;

  const weeklyHours = Math.max(0, args.weeklyHours);
  const monthlyHours = weeklyHours * WEEKS_PER_MONTH;
  const monthlyTotal = service.spitexTariff * monthlyHours;

  if (service.kvgContribution == null) {
    // Hauswirtschaft: fully private.
    return {
      monthlyTotal,
      monthlyKvg: 0,
      monthlyCanton: 0,
      monthlyOutOfPocket: monthlyTotal,
      monthlyHours,
      isKvgCovered: false,
    };
  }

  const monthlyKvg = service.kvgContribution * monthlyHours;
  const gap = Math.max(0, monthlyTotal - monthlyKvg);
  // Canton covers its share of the gap.
  const monthlyCanton = gap * cantonInfo.restfinanzierungShare;

  // Patient co-pay: approximate care-days per month, capped to 30.
  // One care-day = any day on which Spitex visits (heuristic: hours / 1h).
  const careDaysPerMonth = Math.min(30, Math.ceil(weeklyHours) * 4);
  const rawCopay = cantonInfo.appliesCopay
    ? careDaysPerMonth * MAX_COPAY_PER_DAY_CHF
    : 0;
  // Patient-out-of-pocket: whatever the canton does not cover, up to the
  // Art. 25a co-pay cap. Franchise checkbox is acknowledged by the UI but
  // does not alter the Art. 25a tariff math — kept in the formula note.
  const uncoveredGap = Math.max(0, gap - monthlyCanton);
  const monthlyOutOfPocket = Math.min(uncoveredGap + rawCopay, gap);

  // Because canton + patient share = gap (cap), re-derive canton after cap
  // to keep the three bars summing to monthlyTotal.
  const adjustedCanton = gap - monthlyOutOfPocket;

  return {
    monthlyTotal,
    monthlyKvg,
    monthlyCanton: adjustedCanton,
    monthlyOutOfPocket,
    monthlyHours,
    isKvgCovered: true,
  };
}

export function formatCHF(amount: number): string {
  return `CHF ${amount.toLocaleString('de-CH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}
